import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Settings, Trash2, Edit, Server, Shield, Zap } from 'lucide-react';
import { Domain } from '../../types';
import { ServiceSubscription, ServiceStatus } from '../../types/service';
import { getServicesByDomain, deleteService } from '../../services/services';
import { ServiceEditDialog } from './ServiceEditDialog';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';

interface ServiceManagementProps {
  domain: Domain;
  reloadSignal?: number; // trigger refetch when changes
}

export const ServiceManagement: React.FC<ServiceManagementProps> = ({
  domain,
  reloadSignal = 0,
}) => {
  const { t } = useTranslation();
  const [services, setServices] = useState<ServiceSubscription[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<ServiceSubscription | null>(null);

  // Récupérer les services du domaine
  const fetchServices = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getServicesByDomain(domain.id);
      setServices(response.services);
    } catch (error: any) {
      console.error('Error fetching services:', error);
      setError(t('domainServices.errorFetchingServices'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, [domain.id, reloadSignal]);

  const getServiceIcon = (serviceType: string) => {
    switch (serviceType) {
      case 'SSP':
        return <Server className="h-4 w-4" />;
      case 'ADBLOCK':
        return <Shield className="h-4 w-4" />;
      case 'NO_CONSENT':
        return <Zap className="h-4 w-4" />;
      default:
        return <Settings className="h-4 w-4" />;
    }
  };

  const getServiceColor = (serviceType: string) => {
    switch (serviceType) {
      case 'SSP':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'ADBLOCK':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'NO_CONSENT':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  // Déterminer si un service est actif (pas de deletedAt et status ACTIVE)
  const isServiceActive = (service: ServiceSubscription) => {
    return !service.deletedAt && service.status === ServiceStatus.ACTIVE;
  };

  // Obtenir la couleur de la pastille d'état
  const getStatusIndicatorColor = (service: ServiceSubscription) => {
    return isServiceActive(service) ? 'bg-green-500' : 'bg-red-500';
  };

  // Obtenir le texte de l'état
  const getStatusText = (service: ServiceSubscription) => {
    if (service.deletedAt) {
      return `${t('domainServices.deleted')} (${new Date(service.deletedAt).toLocaleDateString()})`;
    }
    return service.status === ServiceStatus.ACTIVE ? t('domainServices.active') : t('domainServices.inactive');
  };

  const handleAddService = () => {
    setSelectedService(null);
    setEditDialogOpen(true);
  };

  const handleEditService = (service: ServiceSubscription) => {
    setSelectedService(service);
    setEditDialogOpen(true);
  };

  const handleDeleteService = async (service: ServiceSubscription) => {
    if (window.confirm(t('domainServices.confirmDeleteService', { service: service.service }))) {
      try {
        await deleteService(service.id);
        await fetchServices(); // Recharger la liste
      } catch (error) {
        console.error('Error deleting service:', error);
        setError(t('domainServices.errorDeletingService'));
      }
    }
  };

  const handleServiceSaved = async () => {
    await fetchServices(); // Recharger la liste après sauvegarde
  };

  return (
    <div className="h-full flex flex-col">
      {/* En-tête avec actions */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold">{t('domainServices.activeServices')}</h3>
          <p className="text-sm text-muted-foreground">
            {isLoading ? t('common.loading') : t('domainServices.servicesCount', { count: services.length })}
          </p>
        </div>
        <Button onClick={handleAddService} size="sm" disabled={isLoading}>
          <Plus className="mr-2 h-4 w-4" />
          {t('domainServices.addService')}
        </Button>
      </div>

      {/* Affichage des erreurs */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>{t('common.error')}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Informations du domaine */}
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{t('domainServices.domainInfo')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">{t('domainServices.domainName')}:</span>
              <span className="ml-2">{domain.name}</span>
            </div>
            <div>
              <span className="font-medium">{t('domainServices.domainUrl')}:</span>
              <span className="ml-2 text-primary">{domain.url}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste des services */}
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="text-muted-foreground">{t('common.loading')}</div>
          </div>
        ) : services.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Server className="h-16 w-16 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold text-muted-foreground mb-2">
              {t('domainServices.noServices')}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mb-4">
              {t('domainServices.addFirstService')}
            </p>
            <Button onClick={handleAddService} variant="outline">
              <Plus className="mr-2 h-4 w-4" />
              {t('domainServices.addService')}
            </Button>
          </div>
        ) : (
          <ScrollArea className="h-full">
            <div className="space-y-3">
              {services.map((service) => (
                <Card key={service.id} className="border-l-4 border-l-primary">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={getServiceColor(service.service)}>
                            <div className="flex items-center gap-1">
                              {getServiceIcon(service.service)}
                              {service.service}
                            </div>
                          </Badge>
                          
                          {/* Pastille d'état actif/inactif */}
                          <div className="flex items-center gap-1">
                            <div className={`h-2 w-2 rounded-full ${getStatusIndicatorColor(service)}`}></div>
                            <span className="text-xs text-muted-foreground">
                              {getStatusText(service)}
                            </span>
                          </div>
                          
                          <Badge variant={service.status === ServiceStatus.ACTIVE ? 'default' : 'secondary'}>
                            {service.status}
                          </Badge>
                          <Badge variant="outline">{service.solution}</Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium">{t('domainServices.startDate')}:</span>
                            <span className="ml-2">{new Date(service.startDate).toLocaleDateString()}</span>
                          </div>
                          <div>
                            <span className="font-medium">{t('domainServices.endDate')}:</span>
                            <span className="ml-2">
                              {service.endDate ? (
                                <span className={service.deletedAt ? 'text-red-600' : ''}>
                                  {new Date(service.endDate).toLocaleDateString()}
                                </span>
                              ) : (
                                <span className="text-muted-foreground italic">{t('domainServices.noEndDate')}</span>
                              )}
                            </span>
                          </div>
                          {service.commissionPercentage && (
                            <div>
                              <span className="font-medium">{t('domainServices.commission')}:</span>
                              <span className="ml-2">{service.commissionPercentage}%</span>
                            </div>
                          )}
                          {service.solution && (
                            <div>
                              <span className="font-medium">Solution:</span>
                              <span className="ml-2">{service.solution}</span>
                            </div>
                          )}
                          {service.gamType && (
                            <div>
                              <span className="font-medium">{t('domainServices.gamType')}:</span>
                              <span className="ml-2">{service.gamType}</span>
                            </div>
                          )}
                          {service.gamAccountId && (
                            <div>
                              <span className="font-medium">{t('domainServices.gamAccountId')}:</span>
                              <span className="ml-2 font-mono text-xs">{service.gamAccountId}</span>
                            </div>
                          )}
                          {service.scriptVariant && (
                            <div>
                              <span className="font-medium">Script Variant:</span>
                              <span className="ml-2 font-mono text-xs">{service.scriptVariant}</span>
                            </div>
                          )}
                          {service.integrationType && (
                            <div>
                              <span className="font-medium">Integration:</span>
                              <span className="ml-2 font-mono text-xs">{service.integrationType}</span>
                            </div>
                          )}
                          {service.protocol && (
                            <div>
                              <span className="font-medium">Protocol:</span>
                              <span className="ml-2 font-mono text-xs">{service.protocol}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-1 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditService(service)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteService(service)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Modale d'édition/ajout de service */}
      <ServiceEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        domain={domain}
        service={selectedService}
        onSave={handleServiceSaved}
      />
    </div>
  );
};